import is from '@sindresorhus/is';
import type { FunctionComponent } from 'react';

import { useTranslate } from '~/shared/context/use-translate';

export type StatusStampKind = 'reviewed' | 'published';

type StatusStampProps = {
    readonly date?: string;
    readonly kind: StatusStampKind;
    readonly localeCode: string;
    readonly muted: boolean;
    readonly testIdPrefix?: string;
    readonly version: number;
};

const dateFormatters = new Map<string, Intl.DateTimeFormat>();

const safeDateFormatter = (localeCode: string): Intl.DateTimeFormat => {
    const cached = dateFormatters.get(localeCode);
    if (!is.undefined(cached)) {
        return cached;
    }
    let formatter: Intl.DateTimeFormat;
    try {
        formatter = new Intl.DateTimeFormat(localeCode, {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
        });
    } catch {
        formatter = new Intl.DateTimeFormat('en-US', {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
        });
    }
    dateFormatters.set(localeCode, formatter);
    return formatter;
};

const formatDate = (iso: string, localeCode: string): string => {
    const parsed = new Date(iso);
    if (Number.isNaN(parsed.getTime())) {
        return '—';
    }
    return safeDateFormatter(localeCode).format(parsed);
};

const isoDateAttribute = (iso: string): string => {
    const parsed = new Date(iso);
    if (Number.isNaN(parsed.getTime())) {
        return iso;
    }
    return parsed.toISOString().slice(0, 10);
};

export const StatusStamp: FunctionComponent<StatusStampProps> = ({
    date,
    kind,
    localeCode,
    muted,
    testIdPrefix = 'job-profile-status-stamp',
    version,
}) => {
    const translate = useTranslate();
    const baseClassName =
        'flex flex-col items-start rounded-[10px] border px-[12px] py-[9px] transition-colors duration-200 ease-out';
    const crispClassName =
        kind === 'reviewed'
            ? 'border-status-info-border bg-status-info-surface text-status-info-text'
            : 'border-status-success-border bg-status-success-surface text-status-success-text';
    const mutedClassName = 'border-border-subtle border-dashed bg-surface-hover/85 text-text-disabled opacity-55';
    const className = `${baseClassName} ${muted ? mutedClassName : crispClassName}`;
    const keywordKey =
        kind === 'reviewed'
            ? 'pages.job-architecture-v2.job-profile-status-stamp.reviewed'
            : 'pages.job-architecture-v2.job-profile-status-stamp.published';

    return (
        <span
            aria-hidden={true}
            className={className}
            data-testid={`${testIdPrefix}-${kind}`}
            data-state={muted ? 'muted' : 'crisp'}
        >
            <span className="text-xs font-bold uppercase leading-none tracking-[0.08em]">{translate(keywordKey)}</span>
            <span className="mt-xs whitespace-nowrap text-[0.6875rem] font-medium leading-tight opacity-70">
                {translate('pages.job-architecture-v2.job-profile-status-stamp.version-prefix')} {version}
                {is.undefined(date) ? null : (
                    <>
                        {' '}
                        · <time dateTime={isoDateAttribute(date)}>{formatDate(date, localeCode)}</time>
                    </>
                )}
            </span>
        </span>
    );
};
